<template>
  <v-row>
    <v-col cols="12" md="6">
      <DetailCard :title="$t('generalInformation')">
        <template #title.append>
          <v-row dense>
            <v-col>
              <Icon
              iconColor="info"
              v-if="tract.requiresExt1"
              icon="mdi-pound"
              margin=""
              :tooltipText="$t('requiresExt1')"/>
            </v-col>
            <v-col>
              <Icon
              iconColor="info"
              v-if="tract.requiresExt2"
              icon="mdi-pound"
              margin=""
              :tooltipText="$t('requiresExt2')"/>
            </v-col>
          </v-row>
        </template>
        <v-row no-gutters v-for="row in generalInformationRows" :key="row.key">
          <v-col cols="6" xs="6" sm="5" >
            {{$t(row.key)}}:
          </v-col>
          <v-col :style="infoCardRightColumn">
            <AccountName
            v-if="row.key === 'defaultLogger' && tract && tract.loggerAccountName"
            :accountId="tract.loggerAccountId"
            :certificationStatus="tract.loggerAccountCertificationStatus"
            color="secondary"
            >
              {{tract.loggerAccountName}}
            </AccountName>
            <span v-else-if="row.key === 'defaultLogger'" :style="{ color: helpers.getTranslucent(25) }">
              {{$t('notSpecified')}}
            </span>
            <span v-else-if="row.key === 'ticketBook'" :style="{ color: row.value === $t('notSpecified') ? helpers.getTranslucent(25) : undefined }">
              {{ row.value }}
              <Icon
              v-if="row.tooltip"
              iconColor="info"
              icon="mdi-account"
              :tooltipText="row.tooltip"/>
            </span>
            <span v-else :style="{ color: row.value === $t('notSpecified') ? helpers.getTranslucent(25) : undefined }">
              {{row.value}}
            </span>
          </v-col>
        </v-row>
        <v-alert
          v-if="missingContractRequirements.length > 0"
          type="warning"
          outlined
          text
          dense
          class="mt-3 mb-0"
          data-testid="tract-missing-contract-requirements">
          {{ $t('tractMissingContractRequirements', { fields: requiredFieldMessages(missingContractRequirements) }) }}
        </v-alert>
      </DetailCard>
    </v-col>
    <v-col cols="12" md="6">
      <DetailCard :title="$t('metrics')">
        <v-row no-gutters v-for="row in metricsRows" :key="row.key">
          <v-col cols="6" sm="5" >
            {{$t(row.key)}}:
          </v-col>
          <v-col cols="6" sm="7" :style="infoCardRightColumn">
            <span :style="{ color: row.value === $t('notSpecified') ? helpers.getTranslucent(25) : undefined }">
              {{row.value}}
              <Icon v-if="row.valueIcon" :icon="row.valueIcon" :tooltipText="row.valueTooltip" iconColor="info" class="ml-1"/>
            </span>
          </v-col>
        </v-row>
      </DetailCard>
    </v-col>
    <v-col cols="12" :lg="tract.users.length > 0 ? 6 : 12" v-if="tract.tractCertifications.length > 0">
      <v-container data-testid="certifications-tract-detail" fluid>
        <v-row dense>
          <v-col cols="12">
            <span class="subtitle-1 black--text">
              {{$t('certifications')}}
            </span>
          </v-col>
          <v-col cols="12">
            <v-row no-gutters align="center" v-for="(certification) in sortedCertifications" :key="certification.certificationId" class="mb-1">
              <v-col cols="auto">
                <v-chip class="mr-2">
                  {{certification.name}}
                </v-chip>
              </v-col>
              <v-col v-if="certification.basisAccountId" class="text--secondary text-caption">
                <Icon
                  v-if="basisExpired(certification)"
                  icon="mdi-alert"
                  iconColor="warning"
                  :tooltipText="$t('basisExpired')"/>
                {{ basisText(certification) }}
              </v-col>
            </v-row>
          </v-col>
        </v-row>
      </v-container>
    </v-col>
    <v-col cols="12" :lg="tract.tractCertifications.length > 0 ? 6 : 12" v-if="tract.users.length > 0">
      <v-container fluid>
        <v-row dense align="center">
          <v-col cols="auto">
            <span class="subtitle-1 black--text" cols="auto">
              {{$t('team')}}
            </span>
          </v-col>
          <v-col>
            <v-chip v-if="tract.teamName" small outlined>{{tract.teamName}}</v-chip>
          </v-col>
          <v-col cols="12">
            <v-chip class="mb-1 mr-1" v-for="(user) in tract.users" :key="user.applicationUserId">
              <Icon v-if="user.applicationUserId === tract.foresterUser?.applicationUserId" icon="mdi-forest" iconColor="rgba(0,0,0,0.6)" :tooltipText="$t('forester')" tooltipColor="black"/>
              {{user.name}}
            </v-chip>
          </v-col>
        </v-row>
      </v-container>
    </v-col>
    <v-col cols="12" :lg="panelSizeForLg" v-if="landowners.length > 0">
      <v-expansion-panels popout tile v-model="openLandownersPanel">
        <v-expansion-panel>
          <v-expansion-panel-header>
            <v-row dense class="subtitle-1 mt-n1" style="flex-wrap: nowrap; overflow: hidden; text-wrap: nowrap;">
              <v-col>
                <span> {{$t('landowners')}} </span>
              </v-col>
              <v-col cols="auto">
                <span> {{ landownersPanelString }}
                </span>
              </v-col>
            </v-row>
          </v-expansion-panel-header>
          <v-expansion-panel-content>
            <v-data-table
            class="mt-2 small-headers"
            hide-default-footer
            :items-per-page="-1"
            :items="landowners"
            :headers="landownerHeaders"
            sort-by="ownership"
            sort-desc
            dense>
              <template #item.name="{item}">
                <v-row no-gutters align="center">
                  {{item.accountName}}
                </v-row>
              </template>
              <template #item.ownership="{item}">
                <span>{{formatPercentage(item.ownership)}}</span>
              </template>
              <template #item.isDefault="{item}">
                <v-icon color="black" v-if="item.isDefault">
                  mdi-checkbox-marked-circle
                </v-icon>
              </template>
            </v-data-table>
          </v-expansion-panel-content>
        </v-expansion-panel>
      </v-expansion-panels>
    </v-col>
    <v-col cols="12" :lg="panelSizeForLg" v-if="tract.loggers.length > 0">
      <v-expansion-panels popout tile v-model="openLoggersPanel">
        <v-expansion-panel >
          <v-expansion-panel-header>
            <v-row dense class="subtitle-1 mt-n1" style="flex-wrap: nowrap; overflow: hidden; text-wrap: nowrap;">
              <v-col>
                <span> {{$t('loggers')}} </span>
              </v-col>
              <v-col cols="auto">
                <span> {{ loggersPanelString }}
                </span>
              </v-col>
            </v-row>
          </v-expansion-panel-header>
          <v-expansion-panel-content>
            <v-data-table
            class="mt-2 small-headers"
            hide-default-footer
            :items-per-page="-1"
            :items="tract.loggers"
            v-if="tract.loggers"
            data-testid="loggers-tract-detail"
            :headers="loggerHeaders"
            dense>
              <template #item.name="{item}">
                <v-row no-gutters align="center">
                  <Icon
                  v-if="item.isDefault"
                  icon="mdi-star"
                  iconColor="rgba(0,0,0,.87)"
                  :tooltipText="$t('defaultLogger')"
                  />
                  <AccountName
                    :accountId="item.accountId"
                    :certificationStatus="item.certificationStatus"
                  >
                    {{item.name}}
                  </AccountName>
                </v-row>
              </template>
              <template #item.code="{item}">
                <span>{{item.code !== '' ? item.code : 'N/A'}}</span>
              </template>
              <template #item.isDefault="{item}">
                <v-icon color="black" v-if="item.isDefault">
                  mdi-checkbox-marked-circle
                </v-icon>
              </template>
            </v-data-table>
          </v-expansion-panel-content>
        </v-expansion-panel>
      </v-expansion-panels>
    </v-col>
    <v-col cols="12" :lg="panelSizeForLg" v-if="tract.ticketBooks.length > 0">
      <v-expansion-panels popout title v-model="openTicketBooksPanel">
        <v-expansion-panel>
          <v-expansion-panel-header>
            <v-row dense class="subtitle-1 mt-n1" style="flex-wrap: nowrap; overflow: hidden; text-wrap: nowrap;">
              <v-col>
                <span> {{$t('ticketBooks')}} </span>
              </v-col>
              <v-col cols="auto">
                <span> {{ tract.ticketBooks[0].accountName }} </span>
              </v-col>
            </v-row>
          </v-expansion-panel-header>
          <v-expansion-panel-content>
            <v-data-table
            class="mt-4"
            hide-default-footer
            :items-per-page="-1"
            :items="mappedTicketBooks"
            :headers="ticketBookHeaders"
            dense>
              <template #item.used="{ item }">
                {{ ticketBookUsage(item) }}
              </template>
            </v-data-table>
          </v-expansion-panel-content>
        </v-expansion-panel>
      </v-expansion-panels>
    </v-col>
  </v-row>
</template>

<script>
import { formatMoney, numberWithCommas, formatPercentage, pageRangeString } from '@/utils/NumericMutations.js'
import { utcToLocalDate } from '@/utils/DateFormatter.js'
import { isBasisExpired, basisCertificationText } from '@/utils/AccountCertifications.js'
import { TractTypeCategory, TractTypeSpecialization } from '@/utils/Enumerations.js'
import { requiredFieldsForTractType, missingRequiredFields, requiredFieldMessages } from '@/utils/TractRequirements.js'
import { mapActions, mapGetters } from 'vuex'
import { helpers } from '@/plugins/theme'
import moment from 'moment'
export default {
  name: 'TractInformation',

  props: {
    tract: {
      type: Object,
      required: true
    },
    landowners: {
      type: Array,
      default: () => []
    }
  },

  data: () => ({
    openLoggersPanel: 0,
    openLandownersPanel: 0,
    openTicketBooksPanel: 0,
    helpers
  }),

  created () {
    if (this.allTractTypes.length === 0) this.fetchTractTypes()
  },

  components: {
    Icon: () => import('@/components/helper/Icon.vue'),
    AccountName: () => import('@/components/account/AccountName.vue'),
    DetailCard: () => import('@/components/core/DetailCard.vue')
  },

  computed: {
    ...mapGetters('tract-type', ['allTractTypes']),

    contractRequirements () {
      const tractType = this.allTractTypes.find(tt => tt.tractTypeId === this.tract.type?.tractTypeId)
      return requiredFieldsForTractType(tractType)
    },

    missingContractRequirements () {
      return missingRequiredFields(this.tract, this.contractRequirements, {
        landowners: this.landowners.length > 0 ? this.landowners : undefined
      })
    },

    isStumpage () {
      return this.categoryFromInt(this.tract.type.category) === this.$t('stumpage')
    },
    costString () {
      if (this.tract.cost <= 0) return this.$t('notSpecified')
      return this.formatMoney(this.tract.cost)
    },

    advanceBalString () {
      return this.formatMoney(this.tract.advanceBalance)
    },

    mapAndParcelNumber () {
      if (!this.tract.mapAndParcelNumber) return this.$t('notSpecified')
      return this.tract.mapAndParcelNumber
    },

    cutoutPercentage () {
      if (this.tract.expectedTons <= 0) return this.$t('notAvailable')
      const { harvestedTons, expectedTons } = this.tract
      const percentage = ((harvestedTons / expectedTons) * 100).toFixed(2)
      return `${percentage}%`
    },

    sortedCertifications () {
      const certifications = this.tract.tractCertifications
      return certifications.sort((a, b) => a.name.localeCompare(b.name))
    },

    loggerHeaders () {
      return [
        {
          text: this.$t('name'),
          value: 'name'
        },
        {
          text: this.$t('code'),
          value: 'code',
          align: 'center'
        }
      ]
    },

    landownerHeaders () {
      return [
        {
          text: this.$t('name'),
          value: 'accountName'
        },
        {
          text: this.$t('ownership'),
          value: 'ownership',
          align: 'right'
        }
      ]
    },

    teamHeaders () {
      return [
        {
          text: this.$t('name'),
          value: 'name'
        }
      ]
    },

    ticketBookHeaders () {
      return [
        {
          text: this.$t('name'),
          value: 'name'
        },
        {
          text: this.$t('pages'),
          value: 'pages'
        },
        {
          text: this.$t('used'),
          value: 'used'
        }
      ]
    },

    infoCardRightColumn () {
      return this.$vuetify.breakpoint.xs ? 'text-align:right;' : ''
    },

    generalInformationRows () {
      const tract = this.tract
      const ns = this.$t('notSpecified')
      return [
        tract.tractGroupName ? { key: 'tractGroup', value: tract.tractGroupName } : undefined,
        { key: 'code', value: tract.code ? tract.code : ns },
        { key: 'type', value: tract.type.name },
        { key: 'category', value: this.categoryFromInt(tract.type.category) },
        { key: 'harvestType', value: tract.harvestType?.name ? tract.harvestType.name : ns },
        { key: 'loggingMethod', value: tract.loggingMethod?.name ? tract.loggingMethod.name : ns },
        tract.type.category === TractTypeCategory.Delivered.value ? { key: 'timberCategory', value: tract.timberCategory?.name ? tract.timberCategory.name : ns } : undefined,
        tract.type.category === TractTypeCategory.Delivered.value ? { key: 'conversion', value: tract.conversion?.name ? tract.conversion.name : ns } : undefined,
        tract.type.category === TractTypeCategory.Delivered.value ? { key: 'landownerType', value: tract.landownerType?.name ? tract.landownerType.name : ns } : undefined,
        { key: 'defaultLogger', value: tract.loggerAccountName },
        { key: 'forester', value: tract.foresterUser?.name ?? ns },
        { key: 'hauler', value: tract.haulerAccount ? tract.haulerAccount : ns },
        { key: 'consultingForester', value: tract.consultingForesterAccount ? tract.consultingForesterAccount : ns },
        { key: 'supplier', value: tract.supplierAccount ? tract.supplierAccount : ns },
        { key: 'contractAccount', value: tract.contractAccount ? tract.contractAccount : ns },
        { key: 'setting', value: tract.contractSetting ? tract.contractSetting : ns },
        tract.type.category === TractTypeCategory.Stumpage.value ? { key: 'businessEntity', value: tract.businessEntity ? tract.businessEntity : ns } : undefined
      ].filter(f => f)
    },

    availabilityPeriodString () {
      const { availabilityStartDate, availabilityEndDate } = this.tract
      if (!availabilityStartDate && !availabilityEndDate) return this.$t('notSpecified')
      const start = availabilityStartDate ? utcToLocalDate(availabilityStartDate) : this.$t('notSpecified')
      const end = availabilityEndDate ? utcToLocalDate(availabilityEndDate) : this.$t('notSpecified')
      return `${start} - ${end}`
    },

    depletionBalanceString () {
      const lastUpdated = this.tract.balanceUpdatedOn ? moment.utc(this.tract.balanceUpdatedOn).local().format('L LT') : this.$t('notAvailable')
      return this.$t('lastUpdatedX', { x: lastUpdated })
    },

    obligation () {
      return this.tract.type.specialization === TractTypeSpecialization.ObligationCapped.value || this.tract.type.specialization === TractTypeSpecialization.ObligationUncapped.value
    },

    metricsRows () {
      const tract = this.tract
      const ns = this.$t('notSpecified')
      const ifn = (cond, v) => cond ? v : undefined
      return [
        ifn(!tract.tractGroupId, { key: this.obligation ? 'obligation' : 'cost', value: this.costString }),
        ifn(!tract.tractGroupId && this.tract.type.specialization === TractTypeSpecialization.Depletion.value, {
          key: 'depletionBalance', value: formatMoney(this.tract.balance), valueIcon: 'mdi-update', valueTooltip: this.depletionBalanceString
        }),
        ifn(!tract.tractGroupId && this.obligation, {
          key: 'obligationBalance', value: formatMoney(this.tract.balance)
        }),
        { key: 'advanceBalance', value: this.advanceBalString },
        { key: 'harvested', value: numberWithCommas(tract.harvestedTons, 3) },
        { key: 'expected', value: numberWithCommas(tract.expectedTons, 3) },
        { key: 'cutoutPercentageLong', value: this.cutoutPercentage },
        { key: 'availabilityPeriod', value: this.availabilityPeriodString },
        { key: 'harvestPeriod', value: `${utcToLocalDate(tract.harvestStartDate)} - ${utcToLocalDate(tract.harvestEndDate)}` },
        ifn(tract.type.category === TractTypeCategory.Stumpage.value, {
          key: 'purchaseDate', value: tract.purchaseDate ? utcToLocalDate(tract.purchaseDate) : this.$t('notSpecified')
        }),
        { key: 'bondBalance', value: formatMoney(tract.bondBalance) },
        { key: 'rpi', value: tract.rpi || ns },
        { key: 'dbh', value: tract.dbh || ns },
        { key: 'age', value: tract.age || ns }
      ].filter(f => f)
    },

    landownersPanelString () {
      return `${this.landowners.reduce((a, lo) => { return lo.ownership > a.ownership ? lo : a }).accountName}${this.landowners?.length > 1 ? ', ' + this.$t('nMore', { n: this.landowners.length - 1 }) : ''}`
    },

    loggersPanelString () {
      return `${this.tract.loggers.find(l => l.isDefault)?.name ?? this.tract.loggers[0]?.name}${this.tract.loggers.length > 1 ? ', ' + this.$t('nMore', { n: this.tract.loggers.length - 1 }) : ''}`
    },

    panelSizeForLg () {
      const show = this.numPanelsShowing
      return 12 / show
    },

    numPanelsShowing () {
      let show = 0
      const showLandowners = !!this.landowners && this.landowners.length > 0
      const showLoggers = !!this.tract.loggers && this.tract.loggers.length > 0
      const showTicketBooks = !!this.tract.ticketBooks && this.tract.ticketBooks.length > 0

      if (showLandowners) show++
      if (showLoggers) show++
      if (showTicketBooks) show++

      return show
    },

    mappedTicketBooks () {
      return this.tract.ticketBooks.map(tb => ({
        ...tb,
        pages: pageRangeString(tb.firstPage, tb.lastPage),
        used: `${tb.availablePageCount} / ${tb.totalPageCount}`
      }))
    }
  },

  methods: {
    ...mapActions('tract-type', ['fetchTractTypes']),
    requiredFieldMessages,
    basisExpired: isBasisExpired,
    basisText: basisCertificationText,

    formatMoney,
    formatPercentage,
    numberWithCommas,
    utcToLocalDate,
    categoryFromInt: (x) => TractTypeCategory.fromInt(x),

    ticketBookUsage (book) {
      const usagePercentage = book.totalPageCount > 0 ? (book.totalPageCount - book.availablePageCount) / book.totalPageCount * 100 : 0
      return `${usagePercentage.toFixed(2)}%`
    }
  }
}
</script>
